import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRIORITY_META, fmtDate, catLabel, hexA, isOverdue } from "./docConstants";
import { StatusBadge } from "./DocBits";
import RequestDialog from "./RequestDialog";

export default function DocumentRequests() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/doc-center/documents", { params: { section: "requested", limit: 300 } });
      setRows(data.documents);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (d) => {
    try { await api.post(`/doc-center/documents/${d.id}/archive`); toast.success("Request cancelled."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="pb-12" data-testid="document-requests-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <button onClick={() => navigate("/documents")} className="text-[12px] text-[#1C4358] hover:underline flex items-center gap-1 mb-1"><Icons.ChevronLeft size={13} /> Document Center</button>
          <h1 className="font-display text-2xl font-bold text-[#1C4358]">Document Requests</h1>
          <p className="text-[13px] text-[#6B7280]">Documents you've asked clients, partners, or professionals to provide.</p>
        </div>
        <Button size="sm" className="h-9 bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid="requests-new-btn" onClick={() => setShowRequest(true)}><Icons.Plus size={15} /> New request</Button>
      </div>

      <div className="rounded-xl border border-[#E6EDF2] bg-white overflow-hidden">
        {loading ? <div className="p-10 text-center text-[#6B7280] text-sm"><Icons.Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
          : !rows.length ? <div className="p-10 text-center text-[#6B7280] text-sm" data-testid="requests-empty">No active document requests.</div>
          : rows.map((d) => (
            <div key={d.id} data-testid={`request-row-${d.id}`} onClick={() => navigate(`/documents/${d.id}`)}
              className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#EEF2F5] last:border-0 hover:bg-[#F7FAFC] cursor-pointer">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Icons.Send size={14} className="text-[#2C6480] shrink-0" />
                  <span className="text-[13.5px] font-semibold text-[#252221] truncate">{d.title}</span>
                  {isOverdue(d) && <span className="text-[10px] font-bold text-[#9C1E22] bg-[#9C1E22]/10 px-1.5 py-0.5 rounded" data-testid={`request-overdue-${d.id}`}>OVERDUE</span>}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-[#6B7280]">
                  <span>{catLabel(d.category)}</span>
                  {d.requested_from_kind && <span>· from {d.requested_from_kind.replace(/_/g, " ")}</span>}
                  {d.due_date && <span>· due {fmtDate(d.due_date)}</span>}
                  {d.priority && <span className="font-semibold" style={{ color: (PRIORITY_META[d.priority] || {}).hex }}>· {(PRIORITY_META[d.priority] || {}).label}</span>}
                </div>
              </div>
              <StatusBadge status={d.status} />
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 text-[12px] text-[#1C4358]" data-testid={`request-open-${d.id}`} onClick={() => navigate(`/documents/${d.id}`)}>Open</Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9C1E22]" title="Cancel request" data-testid={`request-cancel-${d.id}`} onClick={() => cancel(d)}><Icons.Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
      </div>

      <RequestDialog open={showRequest} onOpenChange={setShowRequest} onCreated={load} />
    </div>
  );
}
